import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { formatDateTime } from "./helpers";
import { DialogClose, tNegocio } from "./shared";

export default function ValidateBinancePaymentDialog({ open, saleId, onClose, onPaid }) {
  const dlg = useDialogStyles();
  const [checkout, setCheckout] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !saleId) return undefined;
    setOrderId("");
    setCheckout(null);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post(`/negocio/sales/${saleId}/binance-checkout`);
        if (!cancelled) setCheckout(data);
      } catch (e) {
        if (!cancelled) toastError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, saleId]);

  const confirm = async () => {
    if (!saleId || !orderId.trim()) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/negocio/sales/${saleId}/binance-confirm`, {
        orderId: orderId.trim(),
      });
      setCheckout(data);
      toast.success(tNegocio("pages.binance.paid"));
      onPaid?.(data);
      onClose?.();
    } catch (e) {
      toastError(e);
    } finally {
      setConfirming(false);
    }
  };

  const amount = checkout?.amountUsdt != null ? Number(checkout.amountUsdt).toFixed(2) : "—";
  const paid = checkout?.status === "paid";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      <DialogTitle>{tNegocio("pages.binance.validateTitle")}</DialogTitle>
      <DialogContent dividers>
        {loading && !checkout ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12, opacity: 0.85, fontSize: 13 }}>
              {tNegocio("pages.binance.validateHint")}
            </div>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.binance.payId")}
              value={checkout?.payId || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.binance.amount")}
              value={`${amount} USDT`}
              InputProps={{ readOnly: true }}
              helperText={
                checkout?.expiresAt
                  ? tNegocio("pages.binance.expires").replace("{date}", formatDateTime(checkout.expiresAt))
                  : ""
              }
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.binance.orderId")}
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              helperText={tNegocio("pages.binance.validateOrderHint")}
              disabled={paid}
              autoFocus
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!paid ? (
          <Button
            color="primary"
            variant="contained"
            onClick={confirm}
            disabled={confirming || loading || !orderId.trim()}
          >
            {confirming ? <CircularProgress size={18} color="inherit" /> : tNegocio("pages.binance.confirmOrder")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
